




























































































import {
  computed,
  defineComponent,
  onMounted,
  ref,
  watch,
  nextTick
} from '~/utils/nuxt3-migration'
import { useNamespacedStore } from '~/compositions/store'
import { CLASSIFIED_VIEW_NS } from '~/store/modules/shared/classifieds/view/state'
import { USER_NS, UserState } from '~/store/modules/shared/user/state'
import { useDep } from '~/compositions/dependency-container'
import CSubtitleButton from '~/components/shared/configurable/button/CSubtitleButton.vue'
import RequestAuditModal from '~/components/shared/classified/audits/RequestAuditModal.vue'
import AuditRequestService from '~/services/audits/AuditRequestService'
import CTimeline from '~/components/shared/configurable/timeline/CTimeline.vue'
import CPing from '~/components/shared/configurable/timeline/CPing.vue'
import { AuditRequest, AuditRequestStatusStatus } from '~/models/audit/request'
import CAnonGuardModal from '~/components/shared/configurable/user/CAnonGuardModal.vue'
import CImg from '~/components/shared/configurable/image/CImg.vue'
import { defineComponentTranslations } from '~/utils/i18n'
import AuditProgress from '~/components/shared/classified/audits/AuditProgress.vue'
import CShimmer from '~/components/shared/configurable/shimmer/CShimmer.vue'
import { useScroll } from '~/compositions/scroll'

export default defineComponent({
  components: {
    CAnonGuardModal,
    CPing,
    CTimeline,
    RequestAuditModal,
    CSubtitleButton,
    CImg,
    AuditProgress,
    CShimmer
  },
  props: {
    classifiedId: { type: Number, required: true }
  },
  setup(_, { emit }) {
    const { state, getters, dispatch, commit } = useNamespacedStore(
      CLASSIFIED_VIEW_NS
    )
    const auditRequests = computed(() => {
      return state.auditRequests
    })

    const showRequestAuditModal = ref(false)
    const showAnonGuardModal = ref(false)
    const auditProgressRef = ref(null)
    const urlAuditRequest = ref<AuditRequest | null>(null)
    const { getters: userGetters } = useNamespacedStore<UserState>(USER_NS)
    const { scrollTo } = useScroll()
    const isSingle = computed(() => userGetters('isSingle'))
    const isAnon = computed(() => userGetters('isAnon'))
    const isAuditsUser = computed(() => userGetters('isAuditsUser'))
    const isClassifiedOwner = computed(() => getters('isOwn'))
    const userId = computed(() => userGetters('userId'))
    const hasSelfAuditRequest = computed(() =>
      auditRequests.value?.some(ar => ar.isSelfRequestedAudit)
    )
    const currentUserMadeRequest = computed(() =>
      auditRequests.value?.some(ar => ar.requestedByUser.id === userId.value)
    )

    const userCanManageAudits = computed<boolean>(() => {
      return getters('userIsAdminOrOwner') || isAuditsUser.value
    })
    const auditRequestService = useDep(AuditRequestService)

    const shouldPing = computed<boolean>(() => {
      if (myAuditRequestCurrentStatus.value?.status) {
        return (
          auditRequestService.getStatusVariant(
            myAuditRequestCurrentStatus.value.status,
            0
          ) === 'info'
        )
      }
      return false
    })

    const auditsButtonClick = () => {
      // If the user owns the classified, whatever his type is, he can make an audit request
      if (isClassifiedOwner.value) {
        showRequestAuditModal.value = true

        return
      }

      // If the user is anonymous or single, he can't make an audit request
      if (isAnon.value || isSingle.value) {
        showAnonGuardModal.value = true
        return
      }

      showRequestAuditModal.value = true
    }

    const onRequestAuditModalClose = () => {
      showRequestAuditModal.value = false
    }

    const userHasAuditRequests = computed(() => {
      return Boolean(state.audits?.userHasAuditRequests)
    })
    const minimumPrice = computed(() => {
      return state.audits?.minimumPrice
    })

    const requiresAcceptance = computed(() => {
      return !!state.audits?.requiresAcceptance
    })

    const myAuditRequest = computed<AuditRequest>(() => {
      if (
        !userCanManageAudits.value &&
        userHasAuditRequests.value &&
        state.auditRequests?.length
      ) {
        // always get the latest one
        return state.auditRequests[0]
      }
      return null
    })

    const myAuditRequestCurrentStatus = computed<AuditRequestStatusStatus | null>(
      () => {
        return myAuditRequest.value?.currentStatus || null
      }
    )

    const checkAuditsHash = async () => {
      if (!process.client) {
        return
      }

      const startsWithAudit = window.location.hash.startsWith('#audit-')
      if (
        (startsWithAudit || window.location.hash === '#audits') &&
        (auditRequests.value?.length > 0 || myAuditRequest.value)
      ) {
        if (startsWithAudit) {
          const hashSplit = window.location.hash.split('-')
          if (hashSplit.length > 1) {
            const id = hashSplit[1]
            if (auditRequests.value) {
              urlAuditRequest.value = auditRequests.value.find(
                (r: AuditRequest) => r.id === parseInt(id)
              )
              if (urlAuditRequest.value && auditProgressRef.value?.$el) {
                await nextTick()
                scrollTo(auditProgressRef.value.$el, {
                  duration: 300,
                  easing: 'ease-out',
                  offset: -300
                })
              }
            }
          }
        }
      }
    }
    watch(auditRequests, (val, oldVal) => {
      if (oldVal === null && val?.length > 0) {
        // this means we just loaded something for the first time
        checkAuditsHash()
      }
    })
    onMounted(() => {
      checkAuditsHash()
      emit('mounted')
    })

    const completedRequestsCount = computed(() => {
      if (auditRequests.value) {
        return auditRequests.value.filter(
          (a: AuditRequest) =>
            a.currentStatus.status === AuditRequestStatusStatus.AUDIT_COMPLETED
        ).length
      }
      return 0
    })

    const notCompletedRequestsCount = computed(() => {
      if (auditRequests.value) {
        return auditRequests.value.filter(
          (a: AuditRequest) =>
            a.currentStatus.status !== AuditRequestStatusStatus.AUDIT_COMPLETED
        ).length
      }
      return 0
    })

    async function handleSuccessAuditRequest() {
      await dispatch('loadAuditRequests')
      commit('SET_AUDITS', { ...state.audits, userHasAuditRequests: true })
    }

    function onCreateRequest(auditRequest: AuditRequest) {
      if (auditRequest.payment.paymentUrl) {
        setTimeout(() => {
          window.location.href = auditRequest.payment.paymentUrl!
        }, 1000)
      }

      showRequestAuditModal.value = false
    }

    const showShimmer = computed(() => {
      return (
        (userHasAuditRequests.value && auditRequests.value === null) ||
        state.audits?.available === undefined
      )
    })

    return {
      onCreateRequest,
      auditRequests,
      showAnonGuardModal,
      userCanManageAudits,
      auditsButtonClick,
      showRequestAuditModal,
      myAuditRequest,
      onRequestAuditModalClose,
      checkAuditsHash,
      urlAuditRequest,
      auditRequestService,
      hasSelfAuditRequest,
      myAuditRequestCurrentStatus,
      shouldPing,
      userHasAuditRequests,
      minimumPrice,
      completedRequestsCount,
      notCompletedRequestsCount,
      isClassifiedOwner,
      handleSuccessAuditRequest,
      requiresAcceptance,
      currentUserMadeRequest,
      auditProgressRef,
      showShimmer
    }
  },
  i18n: defineComponentTranslations({
    add_value_to_your_classified: {
      en: 'Add value to your classified by adding an Audit Certificate!',
      el: 'Δώσε αξία στην αγγελία σου με Πιστοποιητικό Τεχνικού Ελέγχου!'
    },
    requires_acceptance_subtitle: {
      en: 'The seller must accept your request.',
      el: 'Ο πωλητής πρέπει να κάνει δεκτό το αίτημά σου'
    },
    requires_acceptance_cta: {
      en: 'Send an audit request',
      el: 'Στείλε αίτημα Τεχνικού Ελέγχου'
    }
  })
})
